import { httpsCallable } from 'firebase/functions';
import type { ChatCitation } from '../types';
import { functions } from './firebase';

export type AskIntelligenceResponse = {
  userMessageId: string;
  assistantMessageId: string;
  reply: string;
  citations: ChatCitation[];
  ragHitCount: number;
};

export function askIntelligenceErrorMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err) {
    const message = String((err as { message: string }).message);
    if (message && !message.startsWith('Firebase')) {
      return message;
    }
  }
  if (err && typeof err === 'object' && 'code' in err) {
    const code = String((err as { code: string }).code);
    if (code === 'functions/invalid-argument') {
      return 'Enter a message before sending.';
    }
    if (code === 'functions/not-found') {
      return 'Chat thread not found. Start a new chat and try again.';
    }
    if (code === 'functions/permission-denied') {
      return 'You do not have permission to use this chat.';
    }
    if (code === 'functions/unauthenticated') {
      return 'Sign in to send a message.';
    }
    if (code === 'functions/unavailable') {
      return 'Intelligence is temporarily unavailable. Try again.';
    }
    if (code === 'functions/failed-precondition') {
      return 'Intelligence is not configured yet. Try again later.';
    }
    if (code === 'functions/internal' || code === 'functions/deadline-exceeded') {
      return 'Could not get a reply. Try again.';
    }
  }
  return err instanceof Error ? err.message : 'Could not send your message.';
}

export async function askIntelligence(
  threadId: string,
  message: string
): Promise<AskIntelligenceResponse> {
  const fn = httpsCallable<{ threadId: string; message: string }, AskIntelligenceResponse>(
    functions,
    'askIntelligence'
  );
  const result = await fn({ threadId: threadId.trim(), message: message.trim() });
  return result.data;
}
